import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Checkbox, Input, Select, Tag, Empty, Space, message } from 'antd';
import {
    ArrowRightOutlined, ArrowLeftOutlined, SearchOutlined, ArrowDownOutlined, ArrowUpOutlined,
    CheckOutlined, CloseOutlined, SettingOutlined, AppstoreOutlined
} from '@ant-design/icons';
import { MALL_APPS, MALL_SELECTABLE_PACKAGES } from '../mallMockData';
import { CostPrice } from './PackagePriceCells';

const getDirectionText = (d) => (d === 1 ? '设备' : d === 2 ? '用户' : '-');
const formatPrice = (v) => `¥${(v / 100).toFixed(2)}`;
// 海外售价为区域定价（海外默认 USD + 国家覆盖），只读来源《套餐配置》
const formatRegionPrice = (regionPricing) => {
    if (!regionPricing || regionPricing.defaultUsd == null) return '';
    const count = (regionPricing.countryOverrides || []).filter(c => c && c.country).length;
    return `$${(regionPricing.defaultUsd / 100).toFixed(2)}${count ? `(${count}国覆盖)` : ''}`;
};

// 套餐价格展示（免费 / 订阅价 / 非订阅价：国内售价 + 海外售价）
const renderPrices = (pkg) => {
    if (pkg.isFree === 1) return <span style={{ color: '#409eff', fontWeight: 500 }}>免费套餐</span>;
    const parts = [];
    if (pkg.isSubscription === 1) {
        const sub = [];
        if (pkg.salePriceCny != null) sub.push(`国内 ${formatPrice(pkg.salePriceCny)}`);
        const region = formatRegionPrice(pkg.subscriptionRegionPricing);
        if (region) sub.push(`海外 ${region}`);
        if (sub.length) parts.push(`订阅价: ${sub.join(' / ')}`);
    }
    if (pkg.isNoSubscription === 1) {
        const noSub = [];
        if (pkg.noSubscriptionPriceCny != null) noSub.push(`国内 ${formatPrice(pkg.noSubscriptionPriceCny)}`);
        const region = formatRegionPrice(pkg.noSubscriptionRegionPricing);
        if (region) noSub.push(`海外 ${region}`);
        if (noSub.length) parts.push(`非订阅价: ${noSub.join(' / ')}`);
    }
    return parts.map(p => <span key={p} style={{ color: '#409eff', fontWeight: 500, marginRight: 12 }}>{p}</span>);
};

/**
 * 商城位运营 - 添加套餐弹窗（筛选 + 左右穿梭 + 目标分类）
 * 真实项目：views/operation/mall/components/AddPackageDialog.vue
 */
const MallAddPackageDialog = ({ open, categories, onCancel, onConfirm, onManageCategory }) => {
    const [filter, setFilter] = useState({ releaseArea: undefined, releaseApp: undefined, operationPosition: undefined, keyword: '' });
    const [selectedPackageIds, setSelectedPackageIds] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [transferMap, setTransferMap] = useState({});       // { packageId: [categoryId] }
    const [cache, setCache] = useState({});                   // { packageId: pkg }
    const [removedExisting, setRemovedExisting] = useState([]); // ['categoryId:packageId']
    const [selectedRightItems, setSelectedRightItems] = useState([]); // [{ categoryId, packageId }] 右侧勾选（用于左移退回）
    const [expanded, setExpanded] = useState([]);

    useEffect(() => {
        if (open) {
            setFilter({ releaseArea: undefined, releaseApp: undefined, operationPosition: undefined, keyword: '' });
            setSelectedPackageIds([]);
            setSelectedCategoryIds([]);
            setTransferMap({});
            setCache({});
            setRemovedExisting([]);
            setSelectedRightItems([]);
            setExpanded([]);
        }
    }, [open]);

    const filteredPackages = useMemo(() => MALL_SELECTABLE_PACKAGES.filter(p => {
        if (filter.releaseArea && !(p.releaseArea || []).includes(filter.releaseArea)) return false;
        if (filter.releaseApp && !(p.releaseApps || []).includes(filter.releaseApp)) return false;
        if (filter.operationPosition && !(p.releaseOpsPosition || []).includes(filter.operationPosition)) return false;
        if (filter.keyword) {
            const kw = filter.keyword.trim().toLowerCase();
            if (!p.name.toLowerCase().includes(kw) && !p.packageId.toLowerCase().includes(kw)) return false;
        }
        return true;
    }), [filter]);

    const existingPackages = (category) => (category.packages || []).filter(pkg => !removedExisting.includes(`${category.id}:${pkg.packageId}`));
    const pendingCount = (categoryId) => Object.values(transferMap).filter(ids => ids.includes(categoryId)).length;
    const pendingPackages = (categoryId) => Object.keys(transferMap)
        .filter(pid => transferMap[pid]?.includes(categoryId))
        .map(pid => cache[pid]).filter(Boolean);

    const togglePackage = (packageId) => setSelectedPackageIds(prev => (prev.includes(packageId) ? prev.filter(i => i !== packageId) : [...prev, packageId]));
    const toggleCategory = (categoryId) => setSelectedCategoryIds(prev => (prev.includes(categoryId) ? prev.filter(i => i !== categoryId) : [...prev, categoryId]));

    // 右侧（目标分类下的套餐）勾选状态
    const isRightSelected = (categoryId, packageId) => selectedRightItems.some(it => it.categoryId === categoryId && it.packageId === packageId);
    const toggleRightItem = (categoryId, packageId) => setSelectedRightItems(prev => (
        prev.some(it => it.categoryId === categoryId && it.packageId === packageId)
            ? prev.filter(it => !(it.categoryId === categoryId && it.packageId === packageId))
            : [...prev, { categoryId, packageId }]
    ));

    // 右移：左侧勾选的套餐 → 右侧勾选的分类；移动后自动展开目标分类，让结果立即可见
    const handleTransfer = () => {
        if (!selectedPackageIds.length || !selectedCategoryIds.length) return;
        const nextMap = { ...transferMap };
        const nextCache = { ...cache };
        let skipped = 0;
        selectedPackageIds.forEach(pid => {
            const pkg = MALL_SELECTABLE_PACKAGES.find(p => p.packageId === pid);
            if (pkg) nextCache[pid] = pkg;
            const list = nextMap[pid] ? [...nextMap[pid]] : [];
            selectedCategoryIds.forEach(cid => {
                // 该分类中已存在此套餐（且未被移除）→ 跳过，避免重复加入
                const cat = categories.find(c => c.id === cid);
                const alreadyIn = (cat?.packages || []).some(p => p.packageId === pid)
                    && !removedExisting.includes(`${cid}:${pid}`);
                if (alreadyIn) { skipped += 1; return; }
                if (!list.includes(cid)) list.push(cid);
            });
            if (list.length) nextMap[pid] = list; else delete nextMap[pid];
        });
        setTransferMap(nextMap);
        setCache(nextCache);
        setSelectedPackageIds([]);
        setSelectedCategoryIds([]);
        setExpanded(prev => [...new Set([...prev, ...selectedCategoryIds])]);
        if (skipped) message.warning('部分套餐已在所选分类中，已自动跳过');
    };

    // 单个退回：待加入 → 撤回待加入；已存在 → 标记从该分类移除
    const removeOne = (categoryId, packageId) => {
        if (transferMap[packageId]?.includes(categoryId)) {
            setTransferMap(prev => {
                const list = (prev[packageId] || []).filter(id => id !== categoryId);
                const next = { ...prev };
                if (list.length) next[packageId] = list; else delete next[packageId];
                return next;
            });
        } else {
            const rk = `${categoryId}:${packageId}`;
            setRemovedExisting(prev => (prev.includes(rk) ? prev : [...prev, rk]));
        }
        setSelectedRightItems(prev => prev.filter(it => !(it.categoryId === categoryId && it.packageId === packageId)));
    };

    // 左移：右侧勾选的套餐退回左侧（取消已加入 / 待加入）
    const handleTransferBack = () => {
        if (!selectedRightItems.length) return;
        const nextMap = { ...transferMap };
        const nextRemoved = [...removedExisting];
        selectedRightItems.forEach(({ categoryId, packageId }) => {
            if (nextMap[packageId]?.includes(categoryId)) {
                const list = nextMap[packageId].filter(id => id !== categoryId);
                if (list.length) nextMap[packageId] = list; else delete nextMap[packageId];
                return;
            }
            const rk = `${categoryId}:${packageId}`;
            if (!nextRemoved.includes(rk)) nextRemoved.push(rk);
        });
        setTransferMap(nextMap);
        setRemovedExisting(nextRemoved);
        setSelectedRightItems([]);
    };

    const handleConfirm = () => {
        const submitCategories = categories.map(category => {
            const existingIds = existingPackages(category).map(pkg => pkg.packageId);
            const pendingIds = Object.keys(transferMap).filter(pid => transferMap[pid]?.includes(category.id));
            return { categoryId: category.id, packageIds: [...new Set([...existingIds, ...pendingIds])] };
        });
        onConfirm(submitCategories);
    };

    const packageCount = (category) => existingPackages(category).length + pendingCount(category.id);

    return (
        <Modal
            title="添加套餐"
            open={open}
            onCancel={onCancel}
            width={1000}
            maskClosable={false}
            destroyOnClose
            footer={
                <Space>
                    <Button icon={<CloseOutlined />} onClick={onCancel}>取消</Button>
                    <Button type="primary" icon={<CheckOutlined />} onClick={handleConfirm}>确定添加</Button>
                </Space>
            }
        >
            {/* 筛选栏 */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: 12 }}>
                <Select placeholder="投放区域" allowClear style={{ width: 150 }} value={filter.releaseArea} onChange={v => setFilter(f => ({ ...f, releaseArea: v }))} options={[{ label: '国内', value: '1' }, { label: '海外', value: '2' }]} />
                <Select placeholder="投放APP" allowClear style={{ width: 150 }} value={filter.releaseApp} onChange={v => setFilter(f => ({ ...f, releaseApp: v }))} options={MALL_APPS.map(a => ({ label: a.appName, value: a.appCode }))} />
                <Select placeholder="运营位" allowClear style={{ width: 150 }} value={filter.operationPosition} onChange={v => setFilter(f => ({ ...f, operationPosition: v }))} options={[{ label: '商城位', value: '商城位' }, { label: '推荐位', value: '推荐位' }]} />
                <Input placeholder="搜索套餐名称或ID" allowClear style={{ width: 200 }} prefix={<SearchOutlined />} value={filter.keyword} onChange={e => setFilter(f => ({ ...f, keyword: e.target.value }))} />
            </div>

            <div style={{ fontSize: 12, color: '#909399', padding: '0 12px 8px' }}>
                勾选左侧套餐 + 右侧分类后点「→」加入；展开分类并勾选其中的套餐后点「←」退回左侧。
            </div>

            <div style={{ display: 'flex', gap: 12, height: 500 }}>
                {/* 左侧：可选套餐 */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #dcdfe6', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: '#f5f7fa', borderBottom: '1px solid #dcdfe6' }}>
                        <span style={{ fontWeight: 600 }}>可选套餐</span>
                        <span style={{ fontSize: 11, color: '#909399' }}>已选 {selectedPackageIds.length}</span>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                        {filteredPackages.map(pkg => (
                            <div key={pkg.packageId} style={{ padding: 10, borderBottom: '1px solid #f0f0f0' }}>
                                <Checkbox checked={selectedPackageIds.includes(pkg.packageId)} onChange={() => togglePackage(pkg.packageId)}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginLeft: 6 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{pkg.name}</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, color: '#909399' }}>
                                            <span style={{ fontFamily: 'monospace' }}>{pkg.packageId}</span>
                                            <span>{getDirectionText(pkg.applicationDirection)}</span>
                                            <span>{(pkg.releaseOpsPosition || []).join(' / ')}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>{renderPrices(pkg)}</div>
                                        <div style={{ fontSize: 11 }}>
                                            <span style={{ color: '#909399' }}>套餐底价：</span>
                                            <CostPrice pkg={pkg} inline />
                                        </div>
                                    </div>
                                </Checkbox>
                            </div>
                        ))}
                        {filteredPackages.length === 0 && <Empty description="暂无可添加的套餐" />}
                    </div>
                </div>

                {/* 中间：双向穿梭按钮 */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '0 4px' }}>
                    <Button
                        type="primary" shape="circle" icon={<ArrowRightOutlined />} title="加入所选分类"
                        disabled={!selectedPackageIds.length || !selectedCategoryIds.length}
                        onClick={handleTransfer}
                    />
                    <Button
                        shape="circle" icon={<ArrowLeftOutlined />} title="退回左侧"
                        disabled={!selectedRightItems.length}
                        onClick={handleTransferBack}
                    />
                </div>

                {/* 右侧：目标分类 */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #dcdfe6', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f5f7fa', borderBottom: '1px solid #dcdfe6' }}>
                        <span style={{ fontWeight: 600 }}>目标分类</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 11, color: '#909399' }}>已选 {selectedRightItems.length}</span>
                            {onManageCategory && (
                                <Button type="link" size="small" icon={<SettingOutlined />} onClick={onManageCategory}>管理</Button>
                            )}
                        </span>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                        {categories.map(category => {
                            const count = packageCount(category);
                            const isExpanded = expanded.includes(category.id);
                            return (
                                <div key={category.id} style={{ padding: 10, border: `1px solid ${selectedCategoryIds.includes(category.id) ? '#409eff' : '#dcdfe6'}`, borderRadius: 4, marginBottom: 6, background: selectedCategoryIds.includes(category.id) ? '#ecf5ff' : '#fff' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Checkbox checked={selectedCategoryIds.includes(category.id)} onChange={() => toggleCategory(category.id)}>
                                            <Space size={6}>
                                                <AppstoreOutlined />
                                                <span style={{ fontWeight: 600, fontSize: 13 }}>{category.name}</span>
                                                <span style={{ fontSize: 11, color: '#909399' }}>{count} 个套餐</span>
                                            </Space>
                                        </Checkbox>
                                        {count > 0 && (
                                            <span style={{ cursor: 'pointer', color: '#909399' }} onClick={() => setExpanded(prev => (prev.includes(category.id) ? prev.filter(i => i !== category.id) : [...prev, category.id]))}>
                                                {isExpanded ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                            </span>
                                        )}
                                    </div>
                                    {isExpanded && count > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                                            {existingPackages(category).map(pkg => (
                                                <Tag
                                                    key={`existing-${pkg.packageId}`}
                                                    icon={isRightSelected(category.id, pkg.packageId) ? <CheckOutlined /> : null}
                                                    closable
                                                    onClose={() => removeOne(category.id, pkg.packageId)}
                                                    onClick={() => toggleRightItem(category.id, pkg.packageId)}
                                                    style={{ cursor: 'pointer', userSelect: 'none', outline: isRightSelected(category.id, pkg.packageId) ? '2px solid #409eff' : 'none', outlineOffset: 1 }}
                                                >{pkg.packageName}</Tag>
                                            ))}
                                            {pendingPackages(category.id).map(pkg => (
                                                <Tag
                                                    key={`pending-${pkg.packageId}`}
                                                    color="blue"
                                                    icon={isRightSelected(category.id, pkg.packageId) ? <CheckOutlined /> : null}
                                                    closable
                                                    onClose={() => removeOne(category.id, pkg.packageId)}
                                                    onClick={() => toggleRightItem(category.id, pkg.packageId)}
                                                    style={{ cursor: 'pointer', userSelect: 'none', outline: isRightSelected(category.id, pkg.packageId) ? '2px solid #409eff' : 'none', outlineOffset: 1 }}
                                                >{pkg.name}</Tag>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {categories.length === 0 && <Empty description="暂无分类，请先创建分类" />}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default MallAddPackageDialog;
